import { escapeHtml, formatQty, formatEntryDate } from './format.js';
import { sectionDocument, type PrintSection } from './printSection.js';

/**
 * Per-salesman item summary, printed on the thermal head straight after the
 * customer receipt when a tenant sets `IsPrintSalesManSummary`.
 *
 * The legacy POS built this in `PrePrintSalesmanSummary`
 * (order_functions_definitions.js:352) and printed it as a second document:
 * group the sold items by salesman, then for each salesman a small header
 * (salesman, customer, date, invoice number) followed by an Item / Qty table.
 * The shop floor uses it to hand each salesman their share of the sale, so the
 * grouping — not the money — is the whole point, and the legacy sheet prints no
 * prices at all. We keep it that way.
 *
 * It is built as a {@link PrintSection} so the cashier can append it to the
 * receipt document and print both on ONE job (one tab, one dialog, a page
 * break — i.e. a cut — between them). `buildSalesmanSummaryHtml` wraps the
 * same section as a standalone document for callers that cannot do that.
 */

export interface SalesmanSummaryItem {
  name: string;
  nameAr?: string | null;
  /** Appended to the item name as "-<barcode>" when the tenant prints it. */
  barcode?: string | null;
  qty: number;
  /** Resolved display name; null lands the item under "Unassigned". */
  salesmanName?: string | null;
}

export interface SalesmanSummaryData {
  /** Cashier-facing receipt number (Inv_Receipt_h.Entry_No). */
  invoiceNo: string | number | null;
  entryDate: string;
  customerName?: string | null;
  items: SalesmanSummaryItem[];
  /** DisplayItemBarcodeinPOSInvoice — legacy reads the same flag here. */
  showBarcode?: boolean;
  /** Arabic labels + RTL, following ArabiReset like the receipt does. */
  arabic?: boolean;
}

const L = {
  en: { title: 'Salesman Summary', salesman: 'Sales Man', customer: 'Customer', date: 'Date', invoice: 'Invoice #', item: 'Item', qty: 'QTY', unassigned: 'Unassigned' },
  ar: { title: 'ملخص المندوبين', salesman: 'المندوب', customer: 'العميل', date: 'التاريخ', invoice: 'رقم الفاتورة', item: 'الصنف', qty: 'الكمية', unassigned: 'غير محدد' },
} as const;

/**
 * Groups items by salesman, preserving first-seen order so the printout
 * follows the order the cashier rang things up in rather than an alphabetical
 * one nobody asked for.
 */
function groupBySalesman(
  items: SalesmanSummaryItem[],
  unassignedLabel: string,
): Array<{ salesman: string; items: SalesmanSummaryItem[] }> {
  const order: string[] = [];
  const byName = new Map<string, SalesmanSummaryItem[]>();
  for (const item of items) {
    const key = item.salesmanName?.trim() || unassignedLabel;
    if (!byName.has(key)) {
      byName.set(key, []);
      order.push(key);
    }
    byName.get(key)!.push(item);
  }
  return order.map((salesman) => ({ salesman, items: byName.get(salesman)! }));
}

/**
 * The summary as an appendable section. Returns null when there is nothing to
 * print, so callers can skip cleanly.
 *
 * The root carries its own `dir`, so an Arabic summary reads right-to-left
 * even when appended to a receipt whose document direction differs.
 */
export function buildSalesmanSummarySection(data: SalesmanSummaryData): PrintSection | null {
  if (data.items.length === 0) return null;

  const ar = data.arabic === true;
  const t = ar ? L.ar : L.en;
  const dir = ar ? 'rtl' : 'ltr';

  const groups = groupBySalesman(data.items, t.unassigned);

  const blocks = groups
    .map((group) => {
      const rows = group.items
        .map((item) => {
          const base = ar ? item.nameAr || item.name : item.name;
          const suffix = data.showBarcode && item.barcode ? `-${item.barcode}` : '';
          return `<tr><td class="sms-item">${escapeHtml(base)}${escapeHtml(suffix)}</td>` +
            `<td class="sms-qty">${escapeHtml(formatQty(item.qty))}</td></tr>`;
        })
        .join('');

      return `<div class="sms-group">
  <div class="sms-meta">
    <div><span class="sms-label">${escapeHtml(t.salesman)}:</span> <b>${escapeHtml(group.salesman)}</b></div>
    ${data.customerName ? `<div><span class="sms-label">${escapeHtml(t.customer)}:</span> ${escapeHtml(data.customerName)}</div>` : ''}
    <div><span class="sms-label">${escapeHtml(t.date)}:</span> ${escapeHtml(formatEntryDate(data.entryDate))}</div>
    ${data.invoiceNo != null ? `<div><span class="sms-label">${escapeHtml(t.invoice)}:</span> ${escapeHtml(String(data.invoiceNo))}</div>` : ''}
  </div>
  <table class="sms-items">
    <thead><tr><th class="sms-item">${escapeHtml(t.item)}</th><th class="sms-qty">${escapeHtml(t.qty)}</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>
</div>`;
    })
    .join('<div class="sms-divider"></div>');

  return {
    css: CSS,
    html: `<div class="sms-doc" lang="${ar ? 'ar' : 'en'}" dir="${dir}">
<div class="sms-title">${escapeHtml(t.title)}</div>
${blocks}
</div>`,
  };
}

/**
 * The summary as a complete document of its own. Null when there is nothing
 * to print.
 */
export function buildSalesmanSummaryHtml(data: SalesmanSummaryData): string | null {
  const section = buildSalesmanSummarySection(data);
  if (!section) return null;
  const ar = data.arabic === true;
  return sectionDocument(section, { title: (ar ? L.ar : L.en).title, lang: ar ? 'ar' : 'en' });
}

/* Scoped under .sms-doc so nothing here touches the receipt it is appended
   to, and none of the receipt's generic rules (.row, .divider, …) touch it. */
const CSS = `
.sms-doc { color: #000; font-size: 12px; }
.sms-title {
  text-align: center; font-size: 15px; font-weight: bold; margin-bottom: 8px;
}
.sms-group { margin-bottom: 6px; }
.sms-meta { font-size: 11px; line-height: 1.5; margin-bottom: 4px; }
.sms-meta .sms-label { color: #000; }
.sms-divider { border-top: 1px dashed #000; margin: 8px 0; }
table.sms-items { width: 100%; border-collapse: collapse; }
table.sms-items th {
  font-size: 11px; text-transform: uppercase; letter-spacing: .3px;
  border-bottom: 1px solid #000; padding: 2px 0;
}
table.sms-items td { padding: 2px 0; vertical-align: top; }
th.sms-item, td.sms-item { text-align: start; width: 70%; }
th.sms-qty, td.sms-qty { text-align: end; width: 30%; font-variant-numeric: tabular-nums; }
`;
